const { polygonArea } = require('../../geometry/floorPolygon');
const { isScanned } = require('../../scanned-label');
const {
  objectIsPlaceholder,
} = require('../../fixture-types/productRepresentation');
const {
  CABINET_SURFACE_TYPES,
  FIXTURE_CATEGORIES,
} = require('../constants/designCenterCategories');
const { isUpperCabinetLikeObject } = require('./fixtureTypeUtils');
const {
  colourCodeFor,
  familyById,
  familyOf,
  familySizes,
  findRunByIdOrAlias,
  priceFor,
  profileOf,
  resolveCabinetSku,
} = require('../../cabinet-runs');
const { priceWithoutColour } = require('../../cabinet-runs/priceList');
const { isTallBody } = require('../../cabinet-runs/keptUnitsInLine');
const { isWoodHood } = require('../../fixture-types/hoodForm');
const { isFloatingShelves } = require('../../cabinet-runs/floatingShelves');
const { isValanceBay } = require('../../cabinet-runs/valanceBays');
const {
  GENERIC_GLASS_HOLDER_FIGURES,
} = require('../../cabinet-runs/glassHolders');
const { roundToEighth } = require('../../dimensioning/inchFractions');
const { ceilingLightStyleOf } = require('./ceilingLightPlan');
const { qualifiersForObject } = require('../../labor/qualifiers');
const { isKitchenSink } = require('../../sink-types');
const { waterfallStoneAreaSqM } = require('./estimate/countertopFabrication');
const { stoneBackTop } = require('../../cabinet-runs/stoneBack');
const { topPieces } = require('./estimate/slabPieces');
const { gradeFromCabinetFamily } = require('./estimate/allowances');
const { isWallMounted } = require('../../group-customization/mountRules');
const {
  ACCESSORY_TYPES,
  isSystemNicheModel,
  systemPartOf,
} = require('../../shower-systems');

// Object types the Design Center's Countertop surface card targets — the
// same pair applyCountertopTexture writes to. These are area-billed (a
// slab or tile countertop is quoted per square foot of top surface).
const COUNTERTOP_SURFACE_TYPES = new Set(['countertop', 'tub-surface']);

// What the take-off BILLS as a top: the Design Center's pair plus the slab
// the kitchen generator lays over an angled corner (`buildCornerFillCountertop`)
// — a real piece of stone the fabricator cuts, which the card never targets.
const BILLED_TOP_TYPES = new Set([
  ...COUNTERTOP_SURFACE_TYPES,
  'corner-fill-countertop',
]);

/**
 * Plan area of a top, m². A top with a polygon (the corner-fill kite) is
 * billed by the polygon — its bounding box is up to twice the stone. Every
 * other top is the rectangle its dimensions describe.
 */
const topAreaSqM = (obj) => {
  const poly = obj.polygonCorners;
  if (Array.isArray(poly) && poly.length >= 3) return polygonArea(poly);
  return obj.dimensions.x * obj.dimensions.z;
};

const M_TO_IN = 39.3701;

/**
 * Collect the area-billed object finishes of a room for the materials
 * take-off: countertop / tub-surface slabs, billed by plan footprint
 * (x × z — the top surface a fabricator quotes per sq ft) plus the stone of
 * any waterfall legs the top turns down to the floor and of any peninsula back
 * cut from it (`waterfallStoneAreaSqM`).
 *
 * Cabinets deliberately do NOT appear here — cabinetry is bought per
 * box, not per square foot of finish, so it's counted by
 * `collectCabinetUnits` instead.
 *
 * Pure: no I/O, callers resolve `textureId` against the catalog.
 *
 * @param {Object} room - Room doc (`objects` and `cabinetRuns` are read).
 * @param {Object} [opts]
 * @param {Object[]} [opts.walls] - the walls that may cap a run end, which
 *   decide where a wall-backed run's waterfall can turn down. Without them
 *   every end of a run reads open.
 * @returns {Array<{textureId, surfaceType, areaSqM, objectId, scanned, pieces, waterfallAreaSqM?}>}
 */
function collectObjectFinishes(room, { walls = [] } = {}) {
  const ctx = topContextOf(room, walls);
  const out = [];
  for (const obj of room?.objects || []) {
    const textureId = obj?.texture?.textureId;
    if (!textureId) continue;
    if (!BILLED_TOP_TYPES.has(obj.type)) continue;

    const areaSqM = billedTopAreaSqM(obj, ctx);
    if (areaSqM === null) continue;

    const waterfallSqM = waterfallStoneAreaSqM(obj, ctx);
    out.push({
      textureId,
      surfaceType: 'countertop',
      areaSqM,
      objectId: obj.id,
      // The scan's own top, unchanged: an existing condition.
      scanned: isScanned(obj),
      pieces: topPieces(obj, ctx),
      // the part of `areaSqM` that is waterfall legs and stone backs, set on
      // a line of its own (`slab-countertop~waterfall`)
      ...(waterfallSqM > 0 ? { waterfallAreaSqM: waterfallSqM } : {}),
    });
  }
  return out;
}

// What a billed top's area is measured against: its room's objects and runs,
// and the walls that may cap a run end.
const topContextOf = (room, walls) => ({
  objects: room?.objects || [],
  cabinetRuns: room?.cabinetRuns || [],
  walls,
});

// A billed top's stone, m²: its plan area plus any waterfall legs and stone
// backs; null for a top with no size.
const billedTopAreaSqM = (obj, ctx) => {
  if (!obj.dimensions) return null;
  const areaSqM = topAreaSqM(obj) + waterfallStoneAreaSqM(obj, ctx);
  return Number.isFinite(areaSqM) && areaSqM > 0 ? areaSqM : null;
};

// The kitchen tops a counter allowance stands in for: a tub's deck slab is
// the bathroom's, and is not one.
const UNCHOSEN_TOP_TYPES = new Set(['countertop', 'corner-fill-countertop']);

/**
 * The NEW kitchen tops nobody has picked a stone for (user, 2026-10-20,
 * items #27 / #42 of a contractor call): a generated kitchen's tops carry no
 * finish until a package or a pick gives them one. The take-off bills them
 * as a countertop ALLOWANCE over the same area the stone would be billed on
 * (`billedTopAreaSqM`). The scan's own top, finish or not, is an existing
 * condition and is never one. Pure.
 *
 * @returns {Array<{surfaceType: 'countertop', areaSqM, objectId}>}
 */
function collectUnchosenTops(room, { walls = [] } = {}) {
  const ctx = topContextOf(room, walls);
  const out = [];
  for (const obj of room?.objects || []) {
    if (obj?.texture?.textureId || isScanned(obj)) continue;
    if (!UNCHOSEN_TOP_TYPES.has(obj?.type)) continue;
    const areaSqM = billedTopAreaSqM(obj, ctx);
    if (areaSqM === null) continue;
    out.push({ surfaceType: 'countertop', areaSqM, objectId: obj.id });
  }
  return out;
}

// ---------------------------------------------------------------------------
// Cabinet units
// ---------------------------------------------------------------------------

// The built-in guess at a cabinet's COST, for a line with no price list — a
// starting number so the take-off isn't blank, always overridable in the
// Price Book. It is the cost book's STANDARD grade, which the spec level or
// a chosen generic line scales (`cabinetGradeFactor`).
//
// A base or wall box is a fixed amount plus a smaller amount per inch
// (2026-10-15): the least-squares line through the cost book's standard-grade
// box prices (NCE 2026 pp.29–30), rounded. The per-inch-only guess it
// replaced ($9/in base, $7/in upper) ran 20–55 % under the book, worst on
// narrow boxes — a box costs its sides, back and hinges whatever its width.
// The vanity's per-inch figure already sat between the book's standard and
// good vanities, and corners and tall surrounds are flat because their price
// is the mechanism or the height, not the width.
const CORNER_BASE_SEED = 350;
const CABINET_PRICE_RULES = {
  'lower-cabinet': { fixed: 120, perInch: 9.5 },
  'upper-cabinet': { fixed: 115, perInch: 5.5 },
  vanity: { perInch: 11, min: 150 },
  'corner-cabinet': { flatBase: CORNER_BASE_SEED, flatUpper: 250 },
  // The polygon base the generator fills an ANGLED corner with. It is a
  // corner base cabinet in a non-square footprint, so it seeds as one.
  'fridge-surround': { flat: 400 },
  'oven-surround': { flat: 400 },
  'laundry-surround': { flat: 400 },
  'blank-panel': { flat: 60 },
};

const roundTo5 = (n) => Math.round(n / 5) * 5;

// A filler is a strip of face material, not a box: it is bought as a stock
// filler, 3" or 6" wide, and ripped to fit on site — so a ¼" filler costs a
// whole 3" piece, and one wider than 6" takes more than one. Per inch of that
// stock, from the Venus 2025-11-01 list (`cabinetPriceLists`), mid colours:
// base BF3/BF6 ~$7.50, wall WF330–WF642 ~$5.50, tall WF396/WF696 ~$20 — the
// same stock a Venus run's fillers are billed as (`resolveSku`). Until
// 2026-10-20 a generic filler was priced as a box ($120 plus $9.50/in), so a
// 0.26" strip cost $120.
const FILLER_STOCK_WIDTHS_IN = [3, 6];
const FILLER_STOCK_PER_INCH = { base: 7.5, upper: 5.5, tall: 20 };

/** Inches of stock a filler of this width is ripped from. */
const fillerStockInches = (widthIn) => {
  const piece = FILLER_STOCK_WIDTHS_IN.find((w) => widthIn <= w);
  if (piece) return piece;
  const widest = FILLER_STOCK_WIDTHS_IN[FILLER_STOCK_WIDTHS_IN.length - 1];
  return widest * Math.ceil(widthIn / widest);
};

/**
 * A filler's seed price: the stock it is cut from — tall when it rises to the
 * upper line beside a tall unit, wall when it hangs with the uppers, base
 * otherwise.
 */
function estimateFillerPrice(obj) {
  // the width the line reads: a 3" filler is 3.000005" in metres and back,
  // and must not be cut from 6" stock for it
  const widthIn = roundToEighth((obj.dimensions?.x ?? 0) * M_TO_IN);
  const kind = isUpperCabinetLikeObject(obj)
    ? 'upper'
    : isTallBody(obj)
      ? 'tall'
      : 'base';
  return roundTo5(fillerStockInches(widthIn) * FILLER_STOCK_PER_INCH[kind]);
}

/**
 * A run's filler strip — what the generator and the run fill mark
 * `category.filler` on a lower or upper cabinet. A `blank-panel` carries the
 * same mark as a gap filler or an end piece and is billed as the panel it is.
 */
const isCabinetFiller = (obj) =>
  obj?.category?.filler === true &&
  (obj.type === 'lower-cabinet' || obj.type === 'upper-cabinet');

/**
 * Rough per-unit price estimate for one cabinet object. Returns null for
 * a type with no rule (callers should have filtered to the cabinet
 * family already).
 */
function estimateCabinetPrice(obj) {
  const rule = CABINET_PRICE_RULES[obj?.type];
  if (!rule) return null;
  if (rule.flatBase !== undefined) {
    return obj.dimensions?.isUpper ? rule.flatUpper : rule.flatBase;
  }
  if (rule.flat !== undefined) return rule.flat;
  const widthIn = (obj.dimensions?.x ?? 0) * M_TO_IN;
  if (rule.fixed !== undefined) {
    return roundTo5(rule.fixed + rule.perInch * widthIn);
  }
  return roundTo5(Math.max(rule.min, rule.perInch * widthIn));
}

// 'lower-cabinet' → 'Lower Cabinet' (label fallback for hand-placed
// cabinets that never got a generator label).
const humanizeType = (type) =>
  type
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

// ---------------------------------------------------------------------------
// Fixtures & appliances
// ---------------------------------------------------------------------------

// Purchasable appliances. Both naming conventions appear in real rooms —
// RoomPlan-scanned objects ('refrigerator', 'stove', 'washerDryer') and
// generator-emitted ones ('fridge', 'range', 'range-hood').
const APPLIANCE_TYPES = new Set([
  'refrigerator',
  'fridge',
  'range',
  'stove',
  'oven',
  // The generator's wall oven (`materializeCandidate`), the type the Design
  // Center's card and `UNIT_MAP` name. Missing here, it was never bought or
  // installed (2026-10-28).
  'wall-oven',
  'cooktop',
  'dishwasher',
  'microwave',
  'range-hood',
  // The kitchen generator's name for the same hood (`buildVentHood`). Missing
  // here, every generated kitchen's hood got no row, no price and no install.
  'vent-hood',
  'washer',
  'dryer',
  'washerDryer',
  'washer-dryer',
]);

// Built-ins the bathroom tools add around a fixture. Not bought from a
// catalog, but real work on the bid: a niche is framed and waterproofed,
// a tub support is a stud platform or bookend under a slab top. Counted
// with the fixtures, but nothing a customer could bring — so never
// customer-supplied with them (`estimate/materialSupply`).
const SITE_BUILT_FIXTURE_TYPES = new Set([
  'shower-niche',
  'tub-support-box',
  // the bench `create_bathroom_shower` builds, framed and tiled in
  'shower-bench',
]);

// The walk-in shower stall: glass and a pan the app assembles to fit
// (`ASSEMBLED_TYPES`), not a product off a shelf. The contractor supplies it
// even where the customer brings the fixtures (user, 2026-10-20) — the
// showerhead and controls in it are fixtures like any other.
const SHOWER_STALL_TYPE = 'shower';

// Purchasable plumbing / lighting / bath-hardware fixtures, plus the
// site-built pieces above. Deliberately NOT furniture (bed / sofa / storage /
// television — scanned contents, not items a renovation buys) and not
// cabinetry (counted by collectCabinetUnits).
const FIXTURE_TYPES = new Set([
  'toilet',
  'sink',
  'kitchen-sink',
  'bathroom-sink',
  'kitchen-faucet',
  'faucet',
  'bathroom-sink-faucet',
  'bathroom-tub-faucet',
  'tub-faucet',
  'bathtub',
  'tub',
  SHOWER_STALL_TYPE,
  'showerhead',
  'shower-control',
  // The scan pipeline and the catalog write the plural (35 corpus objects).
  'shower-controls',
  'mirror',
  'wall-light',
  'overhead-wall-light',
  'ceiling-light',
  'pendant-light',
  'recessed-light',
  'sconce',
  'towel-bar',
  'towel-ring',
  'toilet-paper-holder',
  'robe-hook',
  ...SITE_BUILT_FIXTURE_TYPES,
  // a shower's accessories (shower systems step 3a): a system's shelves,
  // caddies and seats, and the standard grab bar
  ...ACCESSORY_TYPES,
]);

// Every ceiling-light producer numbers the label per instance — the eight
// cans of one grid are stored as "Ceiling Light 1" … "Ceiling Light 8"
// (ceilingLightPlan.js, add_lighting, designPreset, fixtureManipulation).
// The ordinal names an INSTANCE, so it must not reach a schedule row, whose
// identity is a PRODUCT.
const LABEL_ORDINAL = /(?:^|\s)\d+$/;

// The only two variant markers in the data, both on `ceiling-light` objects:
// a recessed can (`proceduralStyle`) and a hanging pendant
// (`lightingSubtype`). Spelled out rather than derived from the string so a
// future non-light `proceduralStyle` can't silently name itself "… Light".
const LIGHT_VARIANT_NAMES = {
  recessed: 'Recessed Light',
  pendant: 'Pendant Light',
};

/**
 * What an unmodeled fixture IS, as a schedule row would name it — the
 * counted-row equivalent of a cabinet's `21" Drawer Base`.
 *
 * A recessed can carries no catalog model: what it is lives in
 * `proceduralStyle` plus its own dimensions (0.1016 m ⇒ a 4" can), never in
 * the label. Reading the label alone made every can in a grid a separate
 * line item; reading its identity makes them one `4" Recessed Light` ×8.
 *
 * @param {Object} obj - Room object.
 * @returns {string}
 */
function fixtureProductName(obj) {
  const variant = obj?.proceduralStyle || obj?.lightingSubtype || null;
  const variantName = variant ? LIGHT_VARIANT_NAMES[variant] : null;
  if (variantName) {
    // A can is bought by its nominal trim size, exactly as a cabinet is
    // bought by its width, so the size belongs in the row name — and in the
    // row identity, keeping 4" and 6" cans on separate lines.
    const diameterM = obj?.dimensions?.x;
    const diameterIn =
      variant === 'recessed' && Number.isFinite(diameterM) && diameterM > 0
        ? Math.round(diameterM * M_TO_IN)
        : null;
    return diameterIn ? `${diameterIn}" ${variantName}` : variantName;
  }
  const humanized = humanizeType(obj?.type || '');
  const name =
    (obj?.label || humanized).replace(LABEL_ORDINAL, '').trim() || humanized;
  // A double-bowl kitchen sink is a different sink to buy (2026-10-27): its
  // own line, named for its two bowls, which its label does not say.
  return isKitchenSink(obj) &&
    obj.basinCount === 2 &&
    !/\b(?:double|dual)\b/i.test(name)
    ? `Double Bowl ${name}`
    : name;
}

const planAreaSqM = (o) => (o?.dimensions?.x ?? 0) * (o?.dimensions?.z ?? 0);

/**
 * The light kind that makes one `ceiling-light` a different PRODUCT from
 * another: a recessed can and a pendant are an order of magnitude apart, and
 * the allowance table prices them separately. Read from the same two fields
 * `fixtureProductName` names the row by, so the name and the price can never
 * disagree about what the fixture is.
 */
const lightVariant = (obj) => {
  const variant = obj?.proceduralStyle || obj?.lightingSubtype || null;
  return variant && LIGHT_VARIANT_NAMES[variant] ? variant : null;
};

/**
 * A procedural mirror that is a frameless MIRROR SURFACE — the full-width,
 * counter-to-ceiling one (`buildVanityMirror`'s `surface`) — rather than a
 * stand-in for a product nobody picked. A catalog mirror is a product,
 * whatever field it carries.
 */
const isMirrorSurface = (obj) =>
  obj?.type === 'mirror' && obj.mirrorKind === 'surface' && !obj.modelId;

/** What BUILD a counted fixture is, where its type covers more than one. */
const fixtureVariant = (obj, byId) => {
  if (obj.type === 'tub-support-box') return tubSupportVariant(obj, byId);
  // a mirror surface is billed on its own glass rate, so it is its own row
  if (isMirrorSurface(obj)) return 'surface';
  return lightVariant(obj);
};

/**
 * Which build a `tub-support-box` is. `shared-lib/tub-surround` writes two
 * under the one type: the stud platform a drop-in tub sits IN, which
 * surrounds the tub ("Tub Support"), and the box closing the gap beside an
 * alcove tub ("Tub Bookend"), a fraction of its size. Told apart by
 * footprint against the parent tub rather than by label. Null when the tub
 * cannot be found, which leaves the install to the type's default.
 */
const tubSupportVariant = (obj, byId) => {
  const tubId = (obj.parents || []).find((p) => p?.type === 'bathtub')?.id;
  const tub = tubId ? byId.get(tubId) : null;
  if (!tub) return null;
  return planAreaSqM(obj) >= planAreaSqM(tub) ? 'platform' : 'bookend';
};

// The ceiling editor's styles the app draws ITSELF — the recessed can and
// the plain puck (`ceilingLightStyleOf`). Its third style, the drum, is a
// catalog GLB.
const APP_BUILT_CEILING_STYLES = new Set(['recessed', 'puck']);

// Fixtures & Finishes rows "assembled procedurally (no single catalog
// model)" that are recognised by their type alone: the shower enclosure,
// built from glass and a pan to fit. The shower-over-tub row is procedural
// only as a COMBO (`match`); its head and valve are catalog products.
const ASSEMBLED_TYPES = new Set(
  FIXTURE_CATEGORIES.filter((c) => c.isProcedural && !c.match).flatMap(
    (c) => c.types
  )
);

/** A shower system's own part: an accessory, or its niche insert. */
const comesWithShowerSystem = (obj) =>
  typeof obj?.showerSystemPart === 'string' ||
  (obj?.type === 'shower-niche' &&
    obj.nicheBuild === 'prefab' &&
    isSystemNicheModel(obj.nicheModel));

/**
 * Can a catalog product be PICKED for this object at all? False for what
 * the app builds itself by design, where no product exists to replace the
 * build: the ceiling editor's recessed cans and pucks, a shower enclosure,
 * a shower system's niche and accessories (they come with the system, step
 * 3b) and the bench (built on site). Such an object is a placeholder by
 * representation (`objectIsPlaceholder`) forever, so an allowance for it
 * could never settle. The standard grab bar is no system's: a product can
 * still be picked for it.
 *
 * The ONE answer the take-off (which never bids such an object as an
 * allowance) and the books' allowance reconcile (which never waits on one,
 * nor counts one as a pick) share — through `collectFixtureUnits`' `pickable`.
 *
 * @param {Object} obj - Room object.
 * @returns {boolean}
 */
const hasCatalogProductPath = (obj) =>
  !ASSEMBLED_TYPES.has(obj?.type) &&
  !APP_BUILT_CEILING_STYLES.has(ceilingLightStyleOf(obj)) &&
  !comesWithShowerSystem(obj) &&
  obj?.type !== 'shower-bench';

const positive = (v) => Number.isFinite(v) && v > 0;

/**
 * One object's size in inches — width, depth and height from its own
 * `dimensions` (x, z, y in metres) — and whether it hangs on a wall
 * (`isWallMounted`), so a scope's "Size:" can be read as width by depth, or
 * by height for what hangs (item #13). Null unless all three are there: a
 * sink written with `width` / `depth` only says no size it can be sure of.
 */
const objectSizeIn = (obj) => {
  const { x, y, z } = obj?.dimensions ?? {};
  if (![x, y, z].every(positive)) return null;
  return {
    w: x * M_TO_IN,
    d: z * M_TO_IN,
    h: y * M_TO_IN,
    wallMounted: isWallMounted(obj),
  };
};

/**
 * What a fixture row (or a pick) is called: the catalog model's own name,
 * else what the object is (`fixtureProductName`, carried on the unit). The
 * take-off's row and the allowance reconcile's picked product read it alike.
 * A shower system's part — its base, its niche — names its maker too
 * ("Shower · Onyx Collection"): it is a row apart from a tile shower's, and
 * two rows both called "Shower" read as one counted twice. A niche insert
 * or an accessory goes by the maker's name for it ("Liberty Wide Niche ·
 * Onyx Collection"), each its own row (step 5). A generic system's part,
 * which has no maker, names the system ("Corner Shelf · Acrylic").
 *
 * @param {{productName: string, systemLabel?: string, systemPart?: string}} unit -
 *   A `collectFixtureUnits` unit.
 * @param {Object|null} model - Its `unifiedObjects` doc, when one resolved.
 * @returns {string}
 */
const fixtureRowName = (unit, model) => {
  const name =
    model?.name ||
    model?.displayName ||
    unit.systemPartName ||
    unit.productName;
  return unit.systemPart && unit.systemLabel
    ? `${name} · ${unit.systemLabel}`
    : name;
};

/**
 * Count the room's fixtures and appliances as unit entries for the
 * take-off. One entry per object; callers group identical
 * (modelId — or type + `productName` when unmodeled) entries into qty rows
 * and resolve `modelId` against `unifiedObjects` for the product name and
 * any catalog price.
 *
 * `label` is the object's own instance name (kept for callers that identify
 * a single object); `productName` is what to group and bill by.
 * `placeholder` says the design still shows the generic for it — no product
 * picked (`objectIsPlaceholder`); `pickable` that a catalog product CAN be
 * picked for it (`hasCatalogProductPath`). Save as estimate bids a row of
 * objects that are both as an allowance.
 *
 * @param {Object} room - Room doc (`objects` and `story` are read).
 * @param {Object} [opts]
 * @param {string|null} [opts.lowestFloor] - the structure's floor-level
 *   setting (`labor/floorLevel.js`), so a tub upstairs reads `story: 'upper'`;
 *   unset is ground.
 * @returns {Array<{type, surface: 'fixture'|'appliance', label, productName, modelId, variant, objectId, placeholder, pickable, qualifiers?, scanned, sizeIn}>}
 */
function collectFixtureUnits(room, { lowestFloor = null } = {}) {
  const objects = room?.objects || [];
  // where the room is, for a fact that depends on it (a tub upstairs)
  const place = { story: room?.story, lowestFloor };
  const byId = new Map(objects.map((o) => [o?.id, o]));
  const out = [];
  for (const obj of objects) {
    // a Venus wood hood is a cabinet shell and an insert (`woodHoodRows`)
    if (isWoodHood(obj)) continue;
    const surface = APPLIANCE_TYPES.has(obj?.type)
      ? 'appliance'
      : FIXTURE_TYPES.has(obj?.type)
        ? 'fixture'
        : null;
    if (!surface) continue;
    // What prices this unit's own install line (a freestanding tub, a shower
    // over 60x36 — shared-lib/labor/qualifiers.js). Absent when it has none,
    // which is most units.
    const qualifiers = qualifiersForObject(obj, objects, place);
    // a shower system's base or niche: priced under its maker, a row apart
    // from a tile shower's (shower systems step 2)
    const systemPart = systemPartOf(obj, objects);
    out.push({
      type: obj.type,
      surface,
      label: obj.label || humanizeType(obj.type),
      productName: fixtureProductName(obj),
      modelId: obj.modelId || null,
      // Picks the install where one type covers two builds (takeoffMapping's
      // VARIANT_UNIT_MAP) and the allowance where it covers two products;
      // null for everything else.
      variant: fixtureVariant(obj, byId),
      objectId: obj.id,
      placeholder: objectIsPlaceholder(obj),
      pickable: hasCatalogProductPath(obj),
      ...(Object.keys(qualifiers).length > 0 ? { qualifiers } : {}),
      // What the scan found and nothing has changed since — an existing
      // condition, priced at nothing (`estimate/existingScope`).
      scanned: isScanned(obj),
      // its size, for a scope's "Size:" (`objectSizeIn`); null when unsure
      sizeIn: objectSizeIn(obj),
      ...(systemPart
        ? {
            brand: systemPart.brand,
            systemPart: systemPart.part,
            // what its row says it is of: the maker, or a generic system's
            // own name (Acrylic), which carries no brand
            systemLabel: systemPart.label,
          }
        : {}),
      // the part itself — a niche insert, an accessory — as its product
      // (step 5): its own row, which a line naming it prices
      ...(systemPart?.id
        ? { systemPartId: systemPart.id, systemPartName: systemPart.name }
        : {}),
    });
  }
  return out;
}

/**
 * The cabinet family the take-off counts: every type the Design Center's
 * cabinet card styles.
 *
 * NOT the corner-fill wedge at an angled corner (`corner-fill-cabinet`; user
 * ruling 2026-11-02): it is filler built on site out of the runs either side,
 * not a box anyone orders — it was billed as a $350 corner base, hung as a
 * base cabinet and matched to a vendor's angled-corner part. Its countertop
 * is still billed by area, and its finished panels as panels.
 */
const TAKEOFF_CABINET_TYPES = new Set([...CABINET_SURFACE_TYPES]);

// A lavatory basin. Its fixture group's cabinet is a vanity; a kitchen sink's
// is a sink base.
const LAV_SINK_TYPES = new Set(['sink', 'bathroom-sink']);

/**
 * What a cabinet object BILLS as. `build_vanity` writes the vanity body as a
 * `lower-cabinet` ("Vanity Cabinet"), so read by type alone a vanity is priced
 * and installed as a kitchen base box. What makes it a vanity is the
 * lavatory sink sharing its fixture group — kitchen cabinets carry fixture
 * groups too (`kitchenCabinet_…`, `kitchenSink_…`), so the group alone is not
 * the signal.
 */
const billedCabinetType = (obj, lavGroupIds) =>
  obj.type === 'lower-cabinet' &&
  obj.fixtureGroupId &&
  lavGroupIds.has(obj.fixtureGroupId)
    ? 'vanity'
    : obj.type;

/**
 * Count the room's cabinetry as priced units for the materials take-off.
 * One entry per cabinet object; callers group identical (label, width,
 * finish) entries into qty rows. Counted whether or not a finish has
 * been applied — the boxes exist either way.
 *
 * `label` is the generator's role label ("Kitchen Sink Cabinet",
 * "Drawer Base", "Upper Cabinet Filler", …) — exactly the granularity a
 * cabinet order is written at — with the humanized type as fallback.
 *
 * @param {Object} room - Room doc (only `objects` is read).
 * @returns {Array<{type, label, widthIn, finishTextureId, seedPrice, variant, objectId}>}
 */
/** How a vendor part that is not a box is installed (`takeoffMapping`). */
const VENDOR_PART_TYPES = {
  'base-filler': 'filler',
  'wall-filler': 'filler',
  'fridge-end-panel': 'end-panel',
  'dishwasher-return': 'end-panel',
  'back-panel': 'end-panel',
  'base-side-panel': 'end-panel',
  'vanity-side-panel': 'end-panel',
};

/**
 * A cabinet in a VENDOR family (one with a size catalog — Venus): its SKU and
 * colour code, and its cost from the family's current price list
 * (design 2026-10-05, D8). Null for any other cabinet — those keep the rough
 * `$/inch` seed. A vendor cabinet with no list price is MISSING
 * (`vendorPrice: null` with the reason), never the seed.
 */
function vendorCabinetPricing(room, obj, priceLists) {
  const run = obj.runId
    ? findRunByIdOrAlias(room?.cabinetRuns, obj.runId)
    : null;
  const family = run ? familyOf(run) : familyById(obj.runFamily);
  if (!familySizes(family)) return null;
  const resolved = resolveCabinetSku(family, obj, {
    profileId: profileOf(run || {}).id,
  });
  if (resolved.accessory) return { accessory: true };
  const code = colourCodeFor(family, obj);
  const list = priceLists?.[family.id] || null;
  // One part's list price, and why it has none — the same ladder for the box
  // and, on a glass upper, the glass door sold for it (plan C10).
  const priced = (part) => {
    const price =
      list && part.sku && code ? priceFor(list, part.sku, code) : null;
    let reason = null;
    if (!part.sku) reason = part.reason;
    else if (!code) reason = `Colour is not a ${family.name} colour`;
    else if (!list) reason = `No ${family.name} price list in this environment`;
    else if (price === null) {
      const colour = family.offering.paint.find((p) => p.code === code);
      reason = `No ${family.name} price for ${part.sku} in ${colour?.name || code}`;
    }
    return { price, reason };
  };
  const { price, reason } = priced(resolved);
  const glass = resolved.glassDoor ? priced(resolved.glassDoor) : null;
  // …and a glass holder under it, in the box's colour too (`glassHolders.js`)
  const holder = resolved.glassHolder ? priced(resolved.glassHolder) : null;
  // A base's roll-out tray, priced with NO colour (user ruling 3): the list
  // carries it under one colour column, never the cabinet's.
  const trayPart = resolved.rollOutTray;
  const trayPrice =
    trayPart && list && trayPart.sku
      ? priceWithoutColour(list, trayPart.sku)
      : null;
  let trayReason = null;
  if (trayPart && trayPrice === null) {
    if (!trayPart.sku) trayReason = trayPart.reason;
    else if (!list)
      trayReason = `No ${family.name} price list in this environment`;
    else trayReason = `No ${family.name} price for ${trayPart.sku}`;
  }
  return {
    familyId: family.id,
    familyName: family.name,
    sku: resolved.sku,
    // one object sold as several units — a Venus fridge column is two end
    // panels (`resolveSku` `fridgeSurroundSku`)
    ...(resolved.count > 1 ? { count: resolved.count } : {}),
    ...(resolved.kind ? { vendorKind: resolved.kind } : {}),
    // stands on a toe-kick base built on site (a 12"-deep run, `resolveSku`)
    ...(resolved.kickBase ? { kickBase: true } : {}),
    colourCode: code,
    vendorPrice: price,
    vendorReason: reason,
    listDate: list ? list.effectiveDate : null,
    // a glass upper's door, its own part (`resolveSku` `glassDoor`)
    ...(glass
      ? {
          glassDoor: {
            sku: resolved.glassDoor.sku,
            vendorPrice: glass.price,
            vendorReason: glass.reason,
          },
        }
      : {}),
    // a glass holder under it, its own part (`resolveSku` `glassHolder`)
    ...(holder
      ? {
          glassHolder: {
            sku: resolved.glassHolder.sku,
            vendorPrice: holder.price,
            vendorReason: holder.reason,
          },
        }
      : {}),
    // a base's roll-out tray part, one per tray (`cabinetRollOutRows.js`)
    ...(trayPart
      ? {
          rollOutTray: {
            sku: trayPart.sku,
            vendorPrice: trayPrice,
            vendorReason: trayReason,
          },
        }
      : {}),
  };
}

/**
 * The generic line someone CHOSE for this cabinet's run — Generic Stock,
 * Semi-Custom or Custom — as `{ lineGrade, lineName }`, or null. That choice
 * is a grade, so it prices the cabinet's seed (`cabinetGradeFactor`). A run
 * with no line set (a scanned run, one from before lines existed) only falls
 * back to a line for its widths, and a line with no grade of its own (Tasoro)
 * has none to give: both leave the cabinet to the job's spec level.
 */
function chosenLine(room, obj) {
  const run = obj.runId
    ? findRunByIdOrAlias(room?.cabinetRuns, obj.runId)
    : null;
  const lineGrade = gradeFromCabinetFamily(run?.family);
  if (!lineGrade) return null;
  return { lineGrade, lineName: familyById(run.family)?.name ?? run.family };
}

function collectCabinetUnits(room, opts = {}) {
  const objects = room?.objects || [];
  const lavGroupIds = new Set(
    objects
      .filter((o) => LAV_SINK_TYPES.has(o?.type) && o.fixtureGroupId)
      .map((o) => o.fixtureGroupId)
  );
  const out = [];
  for (const obj of objects) {
    if (!TAKEOFF_CABINET_TYPES.has(obj?.type)) continue;
    // Floating shelves are boards, billed one each (`floatingShelfRows`) —
    // never the cabinet their bay stands in for.
    if (isFloatingShelves(obj)) continue;
    // A valance bay is the valance's handle: the valance is billed as one
    // (`cabinetTrimRows`), never a cabinet.
    if (isValanceBay(obj)) continue;
    // A peninsula's back under waterfall counters is stone, billed with the
    // counter it is cut from (`stoneBackAreaSqM`) — not a wood panel too.
    if (stoneBackTop(obj, room)) continue;
    const width = obj.dimensions?.x;
    if (!Number.isFinite(width) || width <= 0) continue;
    const type = billedCabinetType(obj, lavGroupIds);
    const vendor = vendorCabinetPricing(room, obj, opts.priceLists);
    // What a vendor family does not sell as a part (a countertop, an open
    // shelf) is priced elsewhere: no cabinet row.
    if (vendor?.accessory) continue;

    // A vendor PART that is not a box is bought, and set, as what it is
    // (`takeoffMapping`): a filler as a filler, a panel — the refrigerator end
    // panels, a dishwasher return, an island's back — as an end panel, never
    // as the cabinet it stands beside. A generic run's filler too: it is a
    // strip, installed in a fraction of a box's time.
    const filler = !vendor && isCabinetFiller(obj);
    const partType =
      VENDOR_PART_TYPES[vendor?.vendorKind] ?? (filler ? 'filler' : null);
    const unit = {
      type: partType ?? type,
      label:
        vendor?.vendorKind === 'fridge-end-panel'
          ? 'Refrigerator end panel'
          : obj.label || humanizeType(obj.type),
      // To the eighth, as a readout would show it: a stock box keeps its whole
      // inches, and a ¼" filler is not a "0\"" one.
      widthIn: roundToEighth(width * M_TO_IN),
      finishTextureId: obj.texture?.textureId ?? null,
      seedPrice: vendor
        ? vendor.vendorPrice
        : filler
          ? estimateFillerPrice(obj)
          : estimateCabinetPrice({ ...obj, type }),
      // Hanging a box on the wall is different work from setting one on the
      // floor, and a corner unit's TYPE does not say which it is.
      variant: isUpperCabinetLikeObject(obj) ? 'upper' : 'base',
      objectId: obj.id,
      // a vendor's cabinet is priced off its list, never the seed
      ...(vendor ? {} : chosenLine(room, obj)),
      ...vendorFieldsOf(vendor),
      // What the scan found and nothing has changed since — an existing
      // condition, priced at nothing (`estimate/existingScope`).
      scanned: isScanned(obj),
    };
    for (let i = 0; i < (vendor?.count ?? 1); i++) out.push(unit);
    if (vendor?.glassDoor) out.push(glassDoorUnit(unit, vendor));
    if (obj.glassHolder === true) out.push(glassHolderUnit(unit, vendor));
  }
  return out;
}

/** A vendor's pricing as unit fields — the glass door rides its own unit. */
const vendorFieldsOf = (vendor) => {
  if (!vendor) return {};
  const { glassDoor: _ownUnit, glassHolder: _ownHolder, ...fields } = vendor;
  return fields;
};

/**
 * A glass holder under an upper (`glassHolders.js`, group 5), as the unit
 * after its box: a Venus line's GL in the box's colour, else the system
 * figure for its width — hung with the box, so no install of its own.
 */
function glassHolderUnit(boxUnit, vendor) {
  const part = vendor?.glassHolder;
  const figure =
    GENERIC_GLASS_HOLDER_FIGURES[Math.round(boxUnit.widthIn)] ?? null;
  return {
    ...boxUnit,
    type: 'glass-holder',
    label: 'Glass holder',
    ...(part
      ? {
          sku: part.sku,
          vendorKind: 'glass-holder',
          seedPrice: part.vendorPrice,
          vendorPrice: part.vendorPrice,
          vendorReason: part.vendorReason,
        }
      : { seedPrice: figure }),
    installedBy: 'hang-upper-cabinet',
  };
}

/**
 * A Venus glass upper's DOOR, as the unit after its box (plan C10): bought
 * as its own part (GDW), in the box's colour, and set with the box — so it
 * carries no install of its own (`installedBy`, plan C15).
 */
function glassDoorUnit(boxUnit, vendor) {
  return {
    ...boxUnit,
    type: 'glass-door',
    label: 'Glass door',
    sku: vendor.glassDoor.sku,
    vendorKind: 'glass-door',
    seedPrice: vendor.glassDoor.vendorPrice,
    vendorPrice: vendor.glassDoor.vendorPrice,
    vendorReason: vendor.glassDoor.vendorReason,
    installedBy: 'hang-upper-cabinet',
  };
}

module.exports = {
  COUNTERTOP_SURFACE_TYPES,
  collectObjectFinishes,
  collectUnchosenTops,
  estimateCabinetPrice,
  APPLIANCE_TYPES,
  FIXTURE_TYPES,
  SITE_BUILT_FIXTURE_TYPES,
  SHOWER_STALL_TYPE,
  fixtureProductName,
  hasCatalogProductPath,
  fixtureRowName,
  collectFixtureUnits,
  isMirrorSurface,
  TAKEOFF_CABINET_TYPES,
  collectCabinetUnits,
};
